import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaStickyNote, FaCopy, FaCheck, FaTrash } from 'react-icons/fa';
import './FloatingNotepad.scss';

const NOTEPAD_DEFAULT_W = 340;
const NOTEPAD_DEFAULT_H = 440;
const NOTEPAD_MIN_W     = 260;
const NOTEPAD_MIN_H     = 260;
const NOTEPAD_MAX_W     = 900;
const NOTEPAD_MAX_H     = 900;
const HEADER_HEIGHT     = 48;

type ResizeDir = 'e' | 's' | 'se';

function clamp(v: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, v));
}

function defaultPos(w: number) {
    return {
        x: clamp(window.innerWidth  - w - 24, 0, window.innerWidth  - w),
        y: clamp(window.innerHeight - NOTEPAD_DEFAULT_H - 24, 0, window.innerHeight - HEADER_HEIGHT),
    };
}

function wordCount(text: string): number {
    return text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
}

interface FloatingNotepadProps {
    open: boolean;
    onClose: () => void;
}

const FloatingNotepad: React.FC<FloatingNotepadProps> = ({ open, onClose }) => {
    const { t } = useTranslation('common');

    // ── Size ───────────────────────────────────────────────────────────────────
    const [size, setSize] = useState<{ width: number; height: number }>(() => {
        try {
            const saved = localStorage.getItem('notepadSize');
            if (saved) {
                const s = JSON.parse(saved) as { width: number; height: number };
                return {
                    width:  clamp(s.width,  NOTEPAD_MIN_W, NOTEPAD_MAX_W),
                    height: clamp(s.height, NOTEPAD_MIN_H, NOTEPAD_MAX_H),
                };
            }
        } catch { /* ignore */ }
        return { width: NOTEPAD_DEFAULT_W, height: NOTEPAD_DEFAULT_H };
    });
    const sizeRef = useRef(size);
    sizeRef.current = size;

    // ── Position ───────────────────────────────────────────────────────────────
    const [pos, setPos] = useState<{ x: number; y: number }>(() => {
        try {
            const saved = localStorage.getItem('notepadPosition');
            if (saved) {
                const p = JSON.parse(saved) as { x: number; y: number };
                return {
                    x: clamp(p.x, 0, window.innerWidth  - NOTEPAD_DEFAULT_W),
                    y: clamp(p.y, 0, window.innerHeight - HEADER_HEIGHT),
                };
            }
        } catch { /* ignore */ }
        return defaultPos(NOTEPAD_DEFAULT_W);
    });
    const posRef = useRef(pos);
    posRef.current = pos;

    // ── Content ────────────────────────────────────────────────────────────────
    const [text, setText] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // ── Clipboard ──────────────────────────────────────────────────────────────
    const [copied, setCopied] = useState(false);
    const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const copyAll = useCallback(() => {
        if (!text) return;
        const confirm = () => {
            if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
            setCopied(true);
            copiedTimerRef.current = setTimeout(() => setCopied(false), 1500);
        };
        if (navigator.clipboard) {
            navigator.clipboard.writeText(text).then(confirm).catch(() => { /* ignore */ });
        } else {
            try {
                const el = document.createElement('textarea');
                el.value = text;
                el.style.cssText = 'position:fixed;opacity:0';
                document.body.appendChild(el);
                el.select();
                document.execCommand('copy');
                document.body.removeChild(el);
                confirm();
            } catch { /* ignore */ }
        }
    }, [text]);

    // Clean up timer on unmount
    useEffect(() => () => { if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current); }, []);

    // ── Escape to close ────────────────────────────────────────────────────────
    useEffect(() => {
        if (!open) return;
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    // Focus textarea when opened
    useEffect(() => {
        if (open) {
            const id = setTimeout(() => textareaRef.current?.focus(), 50);
            return () => clearTimeout(id);
        }
    }, [open]);

    // ── Drag ──────────────────────────────────────────────────────────────────
    const onHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        if ((e.target as HTMLElement).closest('button')) return;
        e.preventDefault();

        const startX    = e.clientX;
        const startY    = e.clientY;
        const startPosX = posRef.current.x;
        const startPosY = posRef.current.y;
        const startW    = sizeRef.current.width;

        const onMove = (ev: MouseEvent) => {
            const newX = clamp(startPosX + ev.clientX - startX, 0, window.innerWidth  - startW);
            const newY = clamp(startPosY + ev.clientY - startY, 0, window.innerHeight - HEADER_HEIGHT);
            posRef.current = { x: newX, y: newY };
            setPos({ x: newX, y: newY });
        };

        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            try { localStorage.setItem('notepadPosition', JSON.stringify(posRef.current)); } catch { /* ignore */ }
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    // ── Resize ────────────────────────────────────────────────────────────────
    const onResizeMouseDown = (e: React.MouseEvent, dir: ResizeDir) => {
        e.preventDefault();
        e.stopPropagation();

        const startX = e.clientX;
        const startY = e.clientY;
        const startW = sizeRef.current.width;
        const startH = sizeRef.current.height;

        const onMove = (ev: MouseEvent) => {
            let newW = startW;
            let newH = startH;

            if (dir === 'e' || dir === 'se') {
                const maxW = window.innerWidth - posRef.current.x;
                newW = clamp(startW + ev.clientX - startX, NOTEPAD_MIN_W, Math.min(NOTEPAD_MAX_W, maxW));
            }
            if (dir === 's' || dir === 'se') {
                const maxH = window.innerHeight - posRef.current.y;
                newH = clamp(startH + ev.clientY - startY, NOTEPAD_MIN_H, Math.min(NOTEPAD_MAX_H, maxH));
            }

            sizeRef.current = { width: newW, height: newH };
            setSize({ width: newW, height: newH });
        };

        const onUp = () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
            try { localStorage.setItem('notepadSize', JSON.stringify(sizeRef.current)); } catch { /* ignore */ }
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    };

    // ── Stats ──────────────────────────────────────────────────────────────────
    const words = wordCount(text);
    const chars = text.length;

    // ── Render ─────────────────────────────────────────────────────────────────
    if (!open) return null;

    return ReactDOM.createPortal(
        <div
            className="floating-notepad"
            style={{ left: pos.x, top: pos.y, width: size.width, height: size.height }}
            role="dialog"
            aria-label={t('notepad.title')}
        >
            {/* ── Header ── */}
            <div className="floating-notepad__header" onMouseDown={onHeaderMouseDown}>
                <FaStickyNote className="floating-notepad__header-icon" />
                <span className="floating-notepad__header-title">{t('notepad.title')}</span>
                <button
                    className="floating-notepad__close"
                    onClick={onClose}
                    aria-label={t('close')}
                >
                    <FaTimes />
                </button>
            </div>

            {/* ── Textarea ── */}
            <div className="floating-notepad__body">
                <textarea
                    ref={textareaRef}
                    className="floating-notepad__textarea"
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder={t('notepad.placeholder')}
                    spellCheck
                    autoCorrect="on"
                />
            </div>

            {/* ── Toolbar ── */}
            <div className="floating-notepad__toolbar">
                <span className="floating-notepad__stats">
                    {t('notepad.wordCount', { count: words })}
                    <span className="floating-notepad__stats-sep">·</span>
                    {t('notepad.charCount', { count: chars })}
                </span>
                <div className="floating-notepad__toolbar-actions">
                    <button
                        className={`floating-notepad__action-btn${copied ? ' floating-notepad__action-btn--copied' : ''}`}
                        onClick={copyAll}
                        disabled={!text}
                        title={copied ? t('calculator.copied') : t('calculator.copyAll')}
                        aria-label={t('calculator.copyAll')}
                    >
                        {copied ? <FaCheck /> : <FaCopy />}
                        <span>{copied ? t('calculator.copied') : t('calculator.copyAll')}</span>
                    </button>
                    <button
                        className="floating-notepad__action-btn floating-notepad__action-btn--danger"
                        onClick={() => setText('')}
                        disabled={!text}
                        title={t('notepad.clear')}
                        aria-label={t('notepad.clear')}
                    >
                        <FaTrash />
                        <span>{t('notepad.clear')}</span>
                    </button>
                </div>
            </div>

            {/* ── Resize handles ── */}
            <div
                className="floating-notepad__resize-e"
                onMouseDown={e => onResizeMouseDown(e, 'e')}
            />
            <div
                className="floating-notepad__resize-s"
                onMouseDown={e => onResizeMouseDown(e, 's')}
            />
            <div
                className="floating-notepad__resize-se"
                onMouseDown={e => onResizeMouseDown(e, 'se')}
            />
        </div>,
        document.body
    );
};

export default FloatingNotepad;

